/**
 * „Neuen Lauf starten“ in der Lauf-Leiste (28.10.2026; Wörter: shared/lauf-starten.ts, Server: server/lauf-starten-route.ts).
 *
 * Martin sah keinen Knopf, wenn Vespera die Mandats-Karte nicht legte. Solange kein Lauf besteht, steht rechts in der Leiste
 * „Neuer Lauf“; er öffnet ein Feld „Aufgabe“ (Martin tippt oder diktiert), die Art aus der festen Liste und „Karte anzeigen“.
 * Der Server legt daraus die vorhandene Mandats-Karte in den Chat — das Mandat erteilt erst sein Tipp auf eine Stufe dort.
 *
 * ⚠️ Das Feld wird NIE vorbelegt: kein Wert vom Server, vom Modell oder aus dem Verlauf; nach dem Senden wird es geleert.
 * ⚠️ Nur ein ECHTER Tipp sendet (`isTrusted`): ein Skript-Klick (Chrome-Erweiterung, browser_click) schickt nichts.
 * ⚠️ Texte nur über textContent; keine Inline-Styles (Aussehen: style.css `.lauf-start …`).
 */
import { UPDATE_KOPF } from "../../shared/update-starten.js";
import {
  AUFGABE_MAX,
  AUFGABE_MIN,
  aufgabeNein,
  istLaufStartFehler,
  LAUF_START_ARTEN,
  LAUF_START_SATZ,
  LAUF_STARTEN_KOPF_WERT,
  LAUF_STARTEN_PFAD,
  type LaufStartFehler,
} from "../../shared/lauf-starten.js";
import { istMandatArt, type MandatArt } from "../../shared/prozess-freigabe.js";

export type LaufStartSenden = (d: { aufgabe: string; art: MandatArt }) => Promise<{ ok: true } | { ok: false; fehler: LaufStartFehler }>;

export const sendeLaufStart: LaufStartSenden = async ({ aufgabe, art }) => {
  try {
    const r = await fetch(LAUF_STARTEN_PFAD, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: { "Content-Type": "application/json", [UPDATE_KOPF]: LAUF_STARTEN_KOPF_WERT },
      body: JSON.stringify({ aufgabe, art, quelle: "tippen" }),
    });
    if (r.ok) return { ok: true };
    const d = (await r.json().catch(() => null)) as { error?: unknown } | null;
    return { ok: false, fehler: istLaufStartFehler(d?.error) ? d.error : "nicht-bereit" };
  } catch {
    return { ok: false, fehler: "nicht-bereit" };
  }
};

export const LAUF_START_GESENDET = "Die Karte steht im Chat. Tippe dort, wie lange ich arbeiten darf.";

export interface LaufStart {
  /** Der Knopf „Neuer Lauf“ (gehört in die Leiste neben den Satz). */
  knopf: HTMLButtonElement;
  /** Der aufklappbare Bereich (gehört als volle Zeile unter den Satz). */
  bereich: HTMLElement;
  /** Darf gerade ein Lauf gestartet werden? Sonst Knopf und Bereich weg. */
  erlaubt(ja: boolean): void;
}

let zaehler = 0;

export function baueLaufStart(doc: Document, senden: LaufStartSenden = sendeLaufStart): LaufStart {
  const n = ++zaehler;
  const knopf = doc.createElement("button");
  knopf.type = "button";
  knopf.className = "lauf-neu";
  knopf.textContent = "Neuer Lauf";
  knopf.setAttribute("aria-expanded", "false");
  knopf.setAttribute("aria-controls", `lauf-start-${n}`);
  knopf.hidden = true;

  const bereich = doc.createElement("div");
  bereich.id = `lauf-start-${n}`;
  bereich.className = "lauf-start";
  bereich.setAttribute("role", "group");
  bereich.setAttribute("aria-label", "Neuen Lauf starten");
  bereich.hidden = true;

  const feldId = `lauf-start-aufgabe-${n}`;
  const hilfeId = `lauf-start-hilfe-${n}`;
  const artId = `lauf-start-art-${n}`;

  const marke = doc.createElement("label");
  marke.className = "lauf-start-marke";
  marke.htmlFor = feldId;
  marke.textContent = "Aufgabe";
  const feld = doc.createElement("textarea");
  feld.id = feldId;
  feld.className = "lauf-start-feld";
  feld.rows = 3;
  feld.maxLength = AUFGABE_MAX;
  feld.minLength = AUFGABE_MIN;
  feld.required = true;
  feld.autocomplete = "off";
  feld.spellcheck = true;
  feld.setAttribute("enterkeyhint", "done");
  feld.setAttribute("aria-describedby", hilfeId);
  const hilfe = doc.createElement("p");
  hilfe.id = hilfeId;
  hilfe.className = "lauf-start-hilfe";

  const artMarke = doc.createElement("label");
  artMarke.className = "lauf-start-marke";
  artMarke.htmlFor = artId;
  artMarke.textContent = "Art";
  const art = doc.createElement("select");
  art.id = artId;
  art.className = "lauf-start-art";
  art.required = true;
  const leer = doc.createElement("option");
  leer.value = "";
  leer.textContent = "Bitte wählen";
  leer.disabled = true;
  leer.selected = true;
  art.append(leer);
  for (const a of LAUF_START_ARTEN) {
    const o = doc.createElement("option");
    o.value = a.art;
    o.textContent = a.titel;
    art.append(o);
  }

  const reihe = doc.createElement("div");
  reihe.className = "lauf-start-reihe";
  const zeigen = doc.createElement("button");
  zeigen.type = "button";
  zeigen.className = "lauf-start-zeigen";
  zeigen.textContent = "Karte anzeigen";
  const abbrechen = doc.createElement("button");
  abbrechen.type = "button";
  abbrechen.className = "lauf-start-abbrechen";
  abbrechen.textContent = "Abbrechen";
  reihe.append(zeigen, abbrechen);

  const meldung = doc.createElement("p");
  meldung.className = "lauf-start-meldung";
  meldung.setAttribute("aria-live", "polite");

  const feldBlock = doc.createElement("div");
  feldBlock.className = "lauf-start-block";
  feldBlock.append(marke, feld, hilfe);
  const artBlock = doc.createElement("div");
  artBlock.className = "lauf-start-block";
  artBlock.append(artMarke, art);
  bereich.append(feldBlock, artBlock, reihe, meldung);

  let darf = false;
  let sendet = false;

  const hilfeSetzen = (): void => {
    const laenge = [...feld.value.trim()].length;
    hilfe.textContent = `Dein Wortlaut steht so auf der Karte. ${laenge} von ${AUFGABE_MAX} Zeichen, mindestens ${AUFGABE_MIN}.`;
  };
  const melden = (text: string, ton: "fehler" | "ok" | ""): void => {
    meldung.textContent = text;
    if (ton) meldung.dataset.ton = ton;
    else delete meldung.dataset.ton;
  };
  const klappeAuf = (auf: boolean): void => {
    bereich.hidden = !auf;
    knopf.setAttribute("aria-expanded", String(auf));
    if (auf) {
      for (const el of [feldBlock, artBlock, reihe]) el.hidden = false;
      hilfeSetzen();
      feld.focus();
    }
  };

  knopf.addEventListener("click", (ev) => {
    if (!ev.isTrusted) return;
    // Nach dem Senden steht nur die Meldung offen — der Knopf öffnet dann ein frisches, leeres Formular.
    const auf = bereich.hidden || feldBlock.hidden;
    if (auf) melden("", "");
    klappeAuf(auf);
  });
  abbrechen.addEventListener("click", () => {
    feld.value = "";
    feld.removeAttribute("aria-invalid");
    melden("", "");
    klappeAuf(false);
    knopf.focus();
  });
  feld.addEventListener("input", () => {
    hilfeSetzen();
    feld.removeAttribute("aria-invalid");
    // Ein alter Fehlersatz gilt nach dem Weitertippen nicht mehr.
    if (meldung.dataset.ton === "fehler") melden("", "");
  });

  zeigen.addEventListener("click", (ev) => {
    // Nur ein echter Tipp (Prüfbefund M1 der Prozess-Freigabe): ein Skript-Klick auf der Seite sendet nie.
    if (!ev.isTrusted || sendet || !darf) return;
    const aufgabe = feld.value.trim();
    const grund = aufgabeNein(aufgabe);
    if (grund) {
      feld.setAttribute("aria-invalid", "true");
      melden(LAUF_START_SATZ[grund], "fehler");
      feld.focus();
      return;
    }
    if (!istMandatArt(art.value)) {
      melden(LAUF_START_SATZ.art, "fehler");
      art.focus();
      return;
    }
    sendet = true;
    zeigen.disabled = true;
    zeigen.textContent = "Lege Karte an …";
    melden("", "");
    void senden({ aufgabe, art: art.value }).then((r) => {
      sendet = false;
      zeigen.disabled = false;
      zeigen.textContent = "Karte anzeigen";
      if (!r.ok) {
        if (r.fehler === "kurz" || r.fehler === "lang" || r.fehler === "zeichen" || r.fehler === "zugangswert" || r.fehler === "leer") {
          feld.setAttribute("aria-invalid", "true");
        }
        melden(LAUF_START_SATZ[r.fehler], "fehler");
        return;
      }
      // Geleert, nie gemerkt: das nächste Öffnen beginnt leer.
      feld.value = "";
      art.value = "";
      for (const el of [feldBlock, artBlock, reihe]) el.hidden = true;
      knopf.setAttribute("aria-expanded", "false");
      melden(LAUF_START_GESENDET, "ok");
      knopf.focus();
    });
  });

  return {
    knopf,
    bereich,
    erlaubt(ja: boolean) {
      if (ja === darf) return;
      darf = ja;
      knopf.hidden = !ja;
      if (!ja) {
        bereich.hidden = true;
        knopf.setAttribute("aria-expanded", "false");
      } else {
        // Ein neuer Abschnitt: Formular wieder vollständig, Meldung weg.
        for (const el of [feldBlock, artBlock, reihe]) el.hidden = false;
        melden("", "");
      }
    },
  };
}
